import { useCallback, useState } from 'react';
import { PageHeader } from '../../layouts/Shells';
import { Badge, Button, EmptyState, Skeleton } from '../../components/ui';
import { SectionRow, SectionRows } from '../../components/ui/SectionRow';
import { Row, RowGroup } from '../../components/ui/RowGroup';
import { OrganizationDialog } from './OrganizationDialog';
import { ReconnectDriveButton, useDriveConnection } from '../auth/ReconnectDrive';
import { PermissionsMatrix } from '../admin/PermissionsMatrix';
import { InvitePanel } from '../admin/InvitePanel';
import { useSetSupplierOrganizationActive, useSupplierOrganizations } from './hooks';
import type { SupplierOrganization } from './types';

/**
 * Minimal supplier-organization administration: list, create, edit safe
 * fields, activate or deactivate. TAP Admin only (route boundary plus RLS).
 * Deliberately no filtering, reporting, bulk operations, or analytics.
 */

function isPermissionError(error: Error): boolean {
  const message = error.message.toLowerCase();
  return message.includes('permission denied') || message.includes('not authorized');
}

/**
 * Google Drive connection state. Uploads, sample photos and pasted
 * screenshots all run on the same Google credential, and it used to die about
 * an hour after sign-in. Connecting once stores a refresh token so the app can
 * mint its own from then on.
 */
function DriveConnectionPanel() {
  const connection = useDriveConnection();
  const connected = connection.data === true;

  return (
    <SectionRow
      title="Google Drive"
      /* The button is the section's action, so it belongs in the anchor beside
         the title — not floating at the end of a sentence, which is the third
         anti-pattern in the brief. */
      action={<ReconnectDriveButton label={connected ? 'Reconnect' : 'Connect Google Drive'} />}
    >
      <RowGroup
        note={
          connected
            ? 'Uploads keep working without signing in again. Reconnect if Drive starts refusing.'
            : 'Uploads stop working about an hour after you sign in until you connect. One trip to the Google permissions screen fixes it for good.'
        }
      >
        <Row
          icon="link"
          label="Drive connection"
          value={
            <Badge tone={connected ? 'success' : 'warning'}>
              {connection.isPending ? 'Checking…' : connected ? 'Connected' : 'Not connected'}
            </Badge>
          }
        />
      </RowGroup>
    </SectionRow>
  );
}

function OrganizationRow({
  organization,
  onEdit,
}: {
  organization: SupplierOrganization;
  onEdit: (organization: SupplierOrganization) => void;
}) {
  const toggle = useSetSupplierOrganizationActive();
  return (
    <Row
      icon="workspace"
      label={organization.name}
      note={`Code: ${organization.code}${organization.contact_email ? ` · ${organization.contact_email}` : ''}`}
      value={
        <Badge tone={organization.is_active ? 'brand' : 'neutral'}>
          {organization.is_active ? 'Active' : 'Inactive'}
        </Badge>
      }
      control={
        <>
          <Button variant="secondary" size="sm" onClick={() => onEdit(organization)}>
            Edit
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={toggle.isPending}
            onClick={() =>
              toggle.mutate({ id: organization.id, isActive: !organization.is_active })
            }
          >
            {organization.is_active ? 'Deactivate' : 'Activate'}
          </Button>
        </>
      }
    />
  );
}

export function AdminOrganizationsPage() {
  const organizations = useSupplierOrganizations();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SupplierOrganization | null>(null);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setEditing(null);
  }, []);

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(organization: SupplierOrganization) {
    setEditing(organization);
    setDialogOpen(true);
  }

  function body() {
    if (organizations.isPending) {
      return <Skeleton lines={4} label="Loading supplier organizations" />;
    }
    if (organizations.isError) {
      return isPermissionError(organizations.error) ? (
        <EmptyState
          title="You do not have access to supplier organizations"
          description="This area is limited to TAP admins."
        />
      ) : (
        <EmptyState
          title="Supplier organizations could not be loaded"
          description="Something went wrong. Try again in a moment."
          action={<Button onClick={() => void organizations.refetch()}>Retry</Button>}
        />
      );
    }
    if (organizations.data.length === 0) {
      return (
        <EmptyState
          title="No supplier organizations yet"
          description="Create the first organization before inviting supplier users."
        />
      );
    }
    return (
      <RowGroup>
        {organizations.data.map((organization) => (
          <OrganizationRow key={organization.id} organization={organization} onEdit={openEdit} />
        ))}
      </RowGroup>
    );
  }

  return (
    <>
      <PageHeader
        title="Admin"
        description="People and invitations, supplier organizations, module permissions and the Google Drive connection."
      />
      {/* One rhythm down the page: four sections on the same 25/75 grid,
          separated by hairlines rather than each sitting in its own box.

          Invitations sit FIRST. It is the only section here somebody arrives
          with a task for — "get Dave in" — where the other three are settings
          you change once and forget. */}
      <SectionRows>
        <InvitePanel />
        <PermissionsMatrix />
        <DriveConnectionPanel />
        <SectionRow
          title="Supplier organizations"
          subtitle="Deactivating an organization removes all supplier access for its users."
          action={
            <Button variant="ghost" size="sm" onClick={openCreate}>
              + Add
            </Button>
          }
        >
          {body()}
        </SectionRow>
      </SectionRows>
      <OrganizationDialog open={dialogOpen} organization={editing} onClose={closeDialog} />
    </>
  );
}
